import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  parametrization,
  products,
  type Modality,
  type ModalityGroup,
  type ModalityGroupInput,
  type ModalityInput,
  type Product,
  type ProductInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatRate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Icon } from '@/shared/ui/Icon';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { MenuDeAcoes } from '../users/MenuDeAcoes';
import { AtivacaoDoRascunho, type ItemEmRascunho } from './AtivacaoDoRascunho';
import { bloqueioDaModalidade, bloqueioDoGrupo } from './bloqueioDaAtivacao';
import { GroupForm, ModalityForm, ProductForm } from './Formularios';
import { gruposDeCreditoDoGrupo, rotuloDe } from './rotulos';
import { Trilha } from './Trilha';

interface NovaModalidade {
  produto: Product;
  grupoId: string;
}

/**
 * Modalidades por ramo e grupo (`/parametrizacao/modalidades`), a "Lista de
 * modalidades" do OnPoint.
 *
 * Cada ramo (o produto do SeguraCore) aparece com o processo SUSEP; dentro dele, cada
 * grupo (Judicial, Tradicional) com a contagem e a lista Modalidade, Status e Acoes.
 * A tela substitui `/produtos` (decisao 4 da secao 11.6 do plano 49) e mantem o que
 * aquela tela fazia: cadastrar e editar produto e modalidade, com a taxa base.
 *
 * O ramo, o grupo e a modalidade em rascunho (carga com aprovacao, P-CARGA) mostram
 * "Ativar" para quem tem `parametrizacao.cadastro.approve`. A ordem e ramo, grupo e
 * modalidade, e quem preparou o rascunho ve o motivo de nao poder ativar.
 */
export function ModalitiesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const podeCriarProduto = can('produtos.produto.create');
  const podeEditarProduto = can('produtos.produto.update');
  const podeExcluirProduto = can('produtos.produto.delete');
  const podeCriarGrupo = can('parametrizacao.modalidade.create');
  const podeEditarGrupo = can('parametrizacao.modalidade.update');
  const podeExcluirGrupo = can('parametrizacao.modalidade.delete');
  const podeAtivarRascunho = can('parametrizacao.cadastro.approve');

  const [criandoProduto, setCriandoProduto] = useState(false);
  const [produtoEmEdicao, setProdutoEmEdicao] = useState<Product | null>(null);
  const [produtoParaExcluir, setProdutoParaExcluir] = useState<Product | null>(null);
  const [grupoNovoEm, setGrupoNovoEm] = useState<Product | null>(null);
  const [grupoEmEdicao, setGrupoEmEdicao] = useState<ModalityGroup | null>(null);
  const [grupoParaExcluir, setGrupoParaExcluir] = useState<ModalityGroup | null>(null);
  const [novaModalidade, setNovaModalidade] = useState<NovaModalidade | null>(null);
  const [paraAtivar, setParaAtivar] = useState<ItemEmRascunho | null>(null);

  const consulta = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['modality-groups'] });
  }

  const criarProduto = useMutation({
    mutationFn: (input: ProductInput) => products.create(input),
    onSuccess: () => {
      toast.success('Ramo cadastrado.');
      setCriandoProduto(false);
      invalidar();
    },
  });

  const atualizarProduto = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProductInput }) =>
      products.update(id, input),
    onSuccess: () => {
      toast.success('Ramo atualizado.');
      setProdutoEmEdicao(null);
      invalidar();
    },
  });

  const excluirProduto = useMutation({
    mutationFn: (id: string) => products.remove(id),
    onSuccess: () => {
      toast.success('Ramo excluido.');
      setProdutoParaExcluir(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setProdutoParaExcluir(null);
    },
  });

  const criarGrupo = useMutation({
    mutationFn: (input: ModalityGroupInput) => parametrization.createGroup(input),
    onSuccess: () => {
      toast.success('Grupo cadastrado.');
      setGrupoNovoEm(null);
      invalidar();
    },
  });

  const atualizarGrupo = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ModalityGroupInput }) =>
      parametrization.updateGroup(id, input),
    onSuccess: () => {
      toast.success('Grupo atualizado.');
      setGrupoEmEdicao(null);
      invalidar();
    },
  });

  const excluirGrupo = useMutation({
    mutationFn: (id: string) => parametrization.removeGroup(id),
    onSuccess: () => {
      toast.success('Grupo excluido.');
      setGrupoParaExcluir(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setGrupoParaExcluir(null);
    },
  });

  const criarModalidade = useMutation({
    mutationFn: ({ productId, input }: { productId: string; input: ModalityInput }) =>
      products.createModality(productId, input),
    onSuccess: () => {
      toast.success('Modalidade cadastrada.');
      setNovaModalidade(null);
      invalidar();
    },
  });

  const ramos = consulta.data ?? [];
  const temRascunho = ramos.some(
    (ramo) =>
      ramo.status === 'rascunho' ||
      (ramo.modality_groups ?? []).some((grupo) => grupo.status === 'rascunho') ||
      ramo.modalities.some((modalidade) => modalidade.status === 'rascunho'),
  );

  return (
    <div className="flex flex-col gap-5">
      <Trilha
        itens={[{ rotulo: 'Parametrizacao', para: '/parametrizacao' }]}
        atual="Lista de modalidades"
      />
      <PageHeader
        title="Modalidades"
        description="Ramos, grupos e modalidades da empresa. Produto e modalidade sao dados, e nao codigo."
        actions={
          podeCriarProduto ? (
            <Button onClick={() => setCriandoProduto(true)}>
              <Icon name="layers" size={15} />
              Novo ramo
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as modalidades">
          {consulta.error.message}
        </Alert>
      ) : null}

      {temRascunho ? (
        <Alert tone="warning" title="Ha itens em rascunho">
          O rascunho nao aparece na cotacao nem recebe operacao. Ele entra em vigor pela
          ativacao de outra pessoa, na ordem ramo, grupo e modalidade, e a modalidade
          precisa da configuracao aprovada.
        </Alert>
      ) : null}

      {consulta.isPending ? (
        <Card>
          <SkeletonRows rows={4} columns={3} />
        </Card>
      ) : ramos.length === 0 ? (
        <Card>
          <EmptyState
            icon="layers"
            title="Nenhum ramo cadastrado"
            description="O ramo e o produto da empresa, como Garantia Segurado - Setor Publico. Ele reune os grupos e as modalidades."
            action={
              podeCriarProduto ? (
                <Button onClick={() => setCriandoProduto(true)}>Cadastrar ramo</Button>
              ) : null
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {ramos.map((ramo) => (
            <SecaoDoRamo
              key={ramo.id}
              ramo={ramo}
              podeEditar={podeEditarProduto}
              podeExcluir={podeExcluirProduto}
              podeCriarGrupo={podeCriarGrupo}
              podeEditarGrupo={podeEditarGrupo}
              podeExcluirGrupo={podeExcluirGrupo}
              podeCriarModalidade={podeCriarProduto}
              podeAtivar={podeAtivarRascunho}
              onAtivar={setParaAtivar}
              onEditar={() => setProdutoEmEdicao(ramo)}
              onExcluir={() => setProdutoParaExcluir(ramo)}
              onCadastrarGrupo={() => setGrupoNovoEm(ramo)}
              onEditarGrupo={setGrupoEmEdicao}
              onExcluirGrupo={setGrupoParaExcluir}
              onAdicionarModalidade={(grupoId) =>
                setNovaModalidade({ produto: ramo, grupoId })
              }
              onAbrirModalidade={(modalidade) =>
                void navigate(`/parametrizacao/modalidades/${modalidade.id}`)
              }
            />
          ))}
        </div>
      )}

      <Modal
        open={criandoProduto}
        onClose={() => setCriandoProduto(false)}
        title="Novo ramo"
        description="O ramo e o produto do SeguraCore."
      >
        <ProductForm
          submitting={criarProduto.isPending}
          error={criarProduto.error}
          onCancel={() => setCriandoProduto(false)}
          onSubmit={(input) => criarProduto.mutate(input)}
        />
      </Modal>

      <Modal
        open={produtoEmEdicao !== null}
        onClose={() => setProdutoEmEdicao(null)}
        title={produtoEmEdicao?.name ?? ''}
      >
        {produtoEmEdicao ? (
          <ProductForm
            product={produtoEmEdicao}
            submitting={atualizarProduto.isPending}
            error={atualizarProduto.error}
            onCancel={() => setProdutoEmEdicao(null)}
            onSubmit={(input) =>
              atualizarProduto.mutate({ id: produtoEmEdicao.id, input })
            }
          />
        ) : null}
      </Modal>

      <Modal
        open={grupoNovoEm !== null}
        onClose={() => setGrupoNovoEm(null)}
        title="Cadastrar grupo de modalidades"
        description={grupoNovoEm ? `No ramo ${grupoNovoEm.name}.` : undefined}
      >
        {grupoNovoEm ? (
          <GroupForm
            ramoEmRascunho={grupoNovoEm.status === 'rascunho'}
            submitting={criarGrupo.isPending}
            error={criarGrupo.error}
            onCancel={() => setGrupoNovoEm(null)}
            onSubmit={(input) =>
              criarGrupo.mutate({ ...input, product_id: grupoNovoEm.id })
            }
          />
        ) : null}
      </Modal>

      <Modal
        open={grupoEmEdicao !== null}
        onClose={() => setGrupoEmEdicao(null)}
        title="Editar grupo"
      >
        {grupoEmEdicao ? (
          <GroupForm
            group={grupoEmEdicao}
            submitting={atualizarGrupo.isPending}
            error={atualizarGrupo.error}
            onCancel={() => setGrupoEmEdicao(null)}
            onSubmit={(input) => atualizarGrupo.mutate({ id: grupoEmEdicao.id, input })}
          />
        ) : null}
      </Modal>

      <Modal
        open={novaModalidade !== null}
        onClose={() => setNovaModalidade(null)}
        title={`Adicionar modalidade em ${novaModalidade?.produto.name ?? ''}`}
      >
        {novaModalidade ? (
          <ModalityForm
            ramoEmRascunho={novaModalidade.produto.status === 'rascunho'}
            groups={novaModalidade.produto.modality_groups ?? []}
            defaultGroupId={novaModalidade.grupoId}
            submitting={criarModalidade.isPending}
            error={criarModalidade.error}
            onCancel={() => setNovaModalidade(null)}
            onSubmit={(input) =>
              criarModalidade.mutate({ productId: novaModalidade.produto.id, input })
            }
          />
        ) : null}
      </Modal>

      <AtivacaoDoRascunho
        item={paraAtivar}
        onFechar={() => setParaAtivar(null)}
        onAtivado={(item) => {
          toast.success(`${item.nome} ativado.`);
          setParaAtivar(null);
          invalidar();
        }}
      />

      <ConfirmDialog
        open={grupoParaExcluir !== null}
        onClose={() => setGrupoParaExcluir(null)}
        onConfirm={() => grupoParaExcluir && excluirGrupo.mutate(grupoParaExcluir.id)}
        title="Deletar grupo"
        message="O grupo sai da lista. So o grupo sem modalidade pode ser excluido; para tirar de uso um grupo com modalidade, inative-o em Editar grupo."
        confirmLabel="Deletar grupo"
        tone="danger"
        loading={excluirGrupo.isPending}
      />

      <ConfirmDialog
        open={produtoParaExcluir !== null}
        onClose={() => setProdutoParaExcluir(null)}
        onConfirm={() =>
          produtoParaExcluir && excluirProduto.mutate(produtoParaExcluir.id)
        }
        title="Excluir ramo"
        message="O ramo sai do cadastro. So o ramo sem modalidade, grupo, seguradora ou corretor ligado pode ser excluido; o ramo em uso e inativado."
        confirmLabel="Excluir ramo"
        tone="danger"
        loading={excluirProduto.isPending}
      />
    </div>
  );
}

function SecaoDoRamo({
  ramo,
  podeEditar,
  podeExcluir,
  podeCriarGrupo,
  podeEditarGrupo,
  podeExcluirGrupo,
  podeCriarModalidade,
  podeAtivar,
  onAtivar,
  onEditar,
  onExcluir,
  onCadastrarGrupo,
  onEditarGrupo,
  onExcluirGrupo,
  onAdicionarModalidade,
  onAbrirModalidade,
}: {
  ramo: Product;
  podeEditar: boolean;
  podeExcluir: boolean;
  podeCriarGrupo: boolean;
  podeEditarGrupo: boolean;
  podeExcluirGrupo: boolean;
  podeCriarModalidade: boolean;
  podeAtivar: boolean;
  onAtivar: (item: ItemEmRascunho) => void;
  onEditar: () => void;
  onExcluir: () => void;
  onCadastrarGrupo: () => void;
  onEditarGrupo: (grupo: ModalityGroup) => void;
  onExcluirGrupo: (grupo: ModalityGroup) => void;
  onAdicionarModalidade: (grupoId: string) => void;
  onAbrirModalidade: (modalidade: Modality) => void;
}) {
  const grupos = ramo.modality_groups ?? [];
  const semGrupo = ramo.modalities.filter((m) => !m.modality_group_id);
  const vazio = ramo.modalities.length === 0 && grupos.length === 0;
  const processo = ramo.susep_process_number ?? '';

  return (
    <Card>
      <CardHeader
        title={`${ramo.name} / Nro Processo SUSEP: ${processo || 'nao informado'}`}
        description={[
          ramo.susep_branch_code ? `Ramo SUSEP ${ramo.susep_branch_code}` : '',
          `Codigo ${ramo.code}`,
          ramo.description,
        ]
          .filter(Boolean)
          .join(' . ')}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="cadastro" value={ramo.status} />
            {podeAtivar && ramo.status === 'rascunho' ? (
              <Button
                size="sm"
                onClick={() =>
                  onAtivar({
                    tipo: 'ramo',
                    id: ramo.id,
                    nome: ramo.name,
                    autor: ramo.draft_authored_by_you === true,
                    autoaprovacao: ramo.draft_may_self_approve === true,
                  })
                }
              >
                Ativar ramo
              </Button>
            ) : null}
            {podeEditar ? (
              <Button size="sm" variant="ghost" onClick={onEditar}>
                Editar ramo
              </Button>
            ) : null}
            {podeExcluir && vazio ? (
              <Button size="sm" variant="ghost" onClick={onExcluir}>
                Excluir ramo
              </Button>
            ) : null}
            {podeCriarGrupo ? (
              <Button size="sm" variant="secondary" onClick={onCadastrarGrupo}>
                Cadastrar grupo
              </Button>
            ) : null}
          </div>
        }
      />
      <CardBody className="flex flex-col gap-4">
        {vazio ? (
          <p className="py-2 text-center text-sm text-ink-500">
            Nenhum grupo nem modalidade neste ramo. Uma operacao precisa de modalidade
            para ser aberta.
          </p>
        ) : null}

        {grupos.map((grupo) => (
          <SecaoDoGrupo
            key={grupo.id}
            titulo={grupo.name}
            grupo={grupo}
            modalidades={ramo.modalities.filter(
              (m) => m.modality_group_id === grupo.id,
            )}
            podeAdicionar={podeCriarModalidade}
            podeEditar={podeEditarGrupo}
            podeExcluir={podeExcluirGrupo}
            podeAtivar={podeAtivar}
            onAtivar={onAtivar}
            bloqueioDoGrupo={bloqueioDoGrupo(ramo)}
            bloqueioDe={(m) => bloqueioDaModalidade(m, ramo, grupo)}
            onAdicionar={() => onAdicionarModalidade(grupo.id)}
            onEditar={() => onEditarGrupo(grupo)}
            onExcluir={() => onExcluirGrupo(grupo)}
            onAbrir={onAbrirModalidade}
          />
        ))}

        {semGrupo.length > 0 || (grupos.length === 0 && !vazio) ? (
          <SecaoDoGrupo
            titulo="Sem grupo"
            modalidades={semGrupo}
            podeAdicionar={podeCriarModalidade}
            podeEditar={false}
            podeExcluir={false}
            podeAtivar={podeAtivar}
            onAtivar={onAtivar}
            bloqueioDe={(m) => bloqueioDaModalidade(m, ramo, undefined)}
            onAdicionar={() => onAdicionarModalidade('')}
            onAbrir={onAbrirModalidade}
          />
        ) : null}

        {vazio && podeCriarModalidade ? (
          <div className="flex justify-center">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onAdicionarModalidade('')}
            >
              Adicionar modalidade
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function SecaoDoGrupo({
  titulo,
  grupo,
  modalidades,
  podeAdicionar,
  podeEditar,
  podeExcluir,
  podeAtivar,
  onAtivar,
  bloqueioDoGrupo: bloqueioDesteGrupo = null,
  bloqueioDe,
  onAdicionar,
  onEditar,
  onExcluir,
  onAbrir,
}: {
  titulo: string;
  grupo?: ModalityGroup | undefined;
  modalidades: Modality[];
  podeAdicionar: boolean;
  podeEditar: boolean;
  podeExcluir: boolean;
  podeAtivar: boolean;
  onAtivar: (item: ItemEmRascunho) => void;
  /** Por que o grupo ainda nao pode ser ativado (R5-B32). */
  bloqueioDoGrupo?: string | null;
  /** Por que a modalidade ainda nao pode ser ativada (R5-B32). */
  bloqueioDe: (modalidade: Modality) => string | null;
  onAdicionar: () => void;
  onEditar?: () => void;
  onExcluir?: () => void;
  onAbrir: (modalidade: Modality) => void;
}) {
  const [aberto, setAberto] = useState(true);
  const quantidade = grupo ? grupo.modality_count : modalidades.length;

  return (
    <section aria-label={`Grupo ${titulo}`} className="rounded-md border border-line">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-surface-sunken px-4 py-2.5">
        <button
          type="button"
          aria-expanded={aberto}
          onClick={() => setAberto((atual) => !atual)}
          className="flex items-center gap-2 text-sm font-semibold text-ink-900"
        >
          <Icon name={aberto ? 'chevronLeft' : 'chevronRight'} size={14} />
          {titulo}
          <Badge tone="neutral">{quantidade}</Badge>
          {grupo && grupo.status !== 'ativo' ? (
            <StatusBadge kind="cadastro" value={grupo.status} />
          ) : null}
          {grupo?.credit_group ? (
            <span className="text-2xs font-normal text-ink-500">
              Credito {rotuloDe(gruposDeCreditoDoGrupo, grupo.credit_group)}
            </span>
          ) : null}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {podeAtivar && grupo?.status === 'rascunho' ? (
            <Button
              size="sm"
              onClick={() =>
                onAtivar({
                  tipo: 'grupo',
                  id: grupo.id,
                  nome: grupo.name,
                  autor: grupo.draft_authored_by_you === true,
                  autoaprovacao: grupo.draft_may_self_approve === true,
                  bloqueio: bloqueioDesteGrupo,
                })
              }
            >
              Ativar grupo
            </Button>
          ) : null}
          {podeAdicionar ? (
            <Button size="sm" variant="secondary" onClick={onAdicionar}>
              Adicionar modalidade
            </Button>
          ) : null}
          {podeEditar && onEditar ? (
            <Button size="sm" variant="ghost" onClick={onEditar}>
              Editar grupo
            </Button>
          ) : null}
          {podeExcluir && onExcluir ? (
            <Button size="sm" variant="ghost" onClick={onExcluir}>
              Deletar grupo
            </Button>
          ) : null}
        </div>
      </header>
      {aberto ? (
        <DataTable
          columns={[
            {
              key: 'modalidade',
              header: 'Modalidade',
              render: (m: Modality) => (
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink-900">{m.name}</p>
                  <p className="truncate text-2xs text-ink-500">
                    {m.code}
                    {m.description ? ` - ${m.description}` : ''}
                  </p>
                </div>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (m: Modality) => <StatusBadge kind="cadastro" value={m.status} />,
            },
            {
              key: 'taxa',
              header: 'Taxa base',
              align: 'right',
              render: (m: Modality) => (
                <span className="tabular-nums">{formatRate(m.base_rate_bps)}</span>
              ),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              render: (m: Modality) => (
                <MenuDeAcoes
                  titulo={`Acoes de ${m.name}`}
                  acoes={[
                    { rotulo: 'Editar', onSelect: () => onAbrir(m) },
                    ...(podeAtivar && m.status === 'rascunho'
                      ? [
                          {
                            rotulo: 'Ativar',
                            onSelect: () =>
                              onAtivar({
                                tipo: 'modalidade',
                                id: m.id,
                                nome: m.name,
                                autor: m.draft_authored_by_you === true,
                                autoaprovacao: m.draft_may_self_approve === true,
                                bloqueio: bloqueioDe(m),
                              }),
                          },
                        ]
                      : []),
                  ]}
                />
              ),
            },
          ]}
          items={modalidades}
          keyOf={(m) => m.id}
          empty={
            <p className="px-5 py-4 text-center text-sm text-ink-500">
              Nenhuma modalidade neste grupo.
            </p>
          }
        />
      ) : null}
    </section>
  );
}
